// Yup schemas for the login/auth surface.
//
// Each schema mirrors the matching BE Pydantic Request model (see
// services/shared/shared_layer/schemas/login.py) 1:1 — same field names,
// same required/optional, same constraints. Update when BE changes.

import * as yup from "yup";

const E164_OR_FREEFORM_PHONE = /^\+?[0-9][0-9\s\-()]{6,}$/;

/**
 * POST /login/check-password — used by LoginPage.
 * BE expects: { email, password }.
 */
export const loginSchema = yup.object({
  email: yup.string().trim().required("Email is required").email("Enter a valid email"),
  password: yup.string().required("Password is required"),
});

/**
 * POST /login/reset-password (or equivalent password-reset endpoint).
 * BE expects: { email }.
 */
export const passwordResetRequestSchema = yup.object({
  email: yup.string().trim().required("Email is required").email("Enter a valid email"),
});

/**
 * POST /interior-designers/signup — used by InteriorDesignerSignup.
 * Mirrors RequestInteriorDesignerAccountData. Address is required-shaped on
 * BE but all individual address fields are themselves optional. Mobile is
 * E.164 from react-phone-number-input.
 */
export const interiorDesignerSignupSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters"),
  email: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobile: yup
    .string()
    .trim()
    .required("Mobile number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid mobile number"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  yearsExperience: yup
    .number()
    .typeError("Years of experience must be a number")
    .integer("Must be a whole number")
    .min(0, "Cannot be negative")
    .required("Years of experience is required"),
  specialization: yup.string().trim().nullable(),
  selfDescription: yup.string().trim().nullable(),
  address: yup
    .object({
      buildingNo: yup.string().trim().nullable(),
      streetName1: yup.string().trim().nullable(),
      streetName2: yup.string().trim().nullable(),
      city: yup.string().trim().nullable(),
      district: yup.string().trim().nullable(),
      state: yup.string().trim().nullable(),
      pinCode: yup.string().trim().nullable(),
      country: yup.string().trim().nullable(),
      addressId: yup.number().default(0),
      addressType: yup.string().default("CUSTOMER"),
    })
    .default({ addressId: 0, addressType: "CUSTOMER", country: "India" }),
});

// Shared address sub-schema for the work-marketplace personas.
const marketplaceAddress = yup
  .object({
    buildingNo: yup.string().trim().nullable(),
    streetName1: yup.string().trim().nullable(),
    streetName2: yup.string().trim().nullable(),
    city: yup.string().trim().nullable(),
    district: yup.string().trim().nullable(),
    state: yup.string().trim().nullable(),
    pinCode: yup.string().trim().nullable(),
    country: yup.string().trim().nullable(),
    addressId: yup.number().default(0),
    addressType: yup.string().default("CUSTOMER"),
  })
  .default({ addressId: 0, addressType: "CUSTOMER", country: "India" });

/**
 * POST /architects/signup — Party A (architect who outsources work).
 * Mirrors RequestArchitectSignup: { name, mobile, email, password, companyName, gstNumber, address }.
 */
export const architectSignupSchema = yup.object({
  name: yup.string().trim().required("Full name is required").min(2, "At least 2 characters"),
  email: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobile: yup
    .string()
    .trim()
    .required("Mobile number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid mobile number"),
  password: yup.string().required("Password is required").min(8, "At least 8 characters"),
  companyName: yup.string().trim().nullable(),
  gstNumber: yup.string().trim().nullable(),
  address: marketplaceAddress,
});

/**
 * POST /freelancers/signup — Party B (architecture student / intern / fresher).
 * Mirrors RequestFreelancerSignup: { name, mobile, email, password, address }. Skills are captured
 * post-signup on the Skills page.
 */
export const freelancerSignupSchema = yup.object({
  name: yup.string().trim().required("Full name is required").min(2, "At least 2 characters"),
  email: yup.string().trim().required("Email is required").email("Enter a valid email"),
  mobile: yup
    .string()
    .trim()
    .required("Mobile number is required")
    .matches(E164_OR_FREEFORM_PHONE, "Enter a valid mobile number"),
  password: yup.string().required("Password is required").min(8, "At least 8 characters"),
  address: marketplaceAddress,
});
